import { Plus } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import type { JSX } from "react";

import type { FormColumn as ServiceFormColumn } from "../../services/calculatorService";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { AddRowTable, type AddRowTableProps } from "./AddRowTable";
import {
  EQUIPMENT_TYPES,
  EQUIPMENT_TYPE_FIELD_KEY,
  type EquipmentType,
  equipmentRowNames,
  fieldKeyOf,
  isEquipmentType,
  rowValuesByFieldKey,
  visibleFieldKeysFor,
} from "./heavyEquipmentCatalogue";

/**
 * Heavy Equipment as one small table per kind of equipment.
 *
 * Maz asked for Cold Storage's shape, every question on one row. Heavy
 * Equipment cannot do that with one table: 55 columns are served and a row uses
 * 5 to 10, so a single table is mostly empty cells. Grouping by type makes each
 * table narrow enough to read, which is what she agreed to on #362.
 *
 * Rows are added already typed, into their group, and groups keep the order
 * their type was first added, so nothing on screen moves.
 */

export interface HeavyEquipmentGroupsProps extends AddRowTableProps {
  /** Adds a row of this type and returns its id. Absent when the row set is
   * fixed, as in savings. */
  onAddRow?: (equipmentType: EquipmentType) => string;
}

interface Group {
  key: string;
  type: EquipmentType | null;
  rows: AddRowTableProps["rows"];
}

/**
 * The columns a group shows: the union of what its own rows are asked.
 *
 * A union rather than the type's full set, because follow-ups differ row to row
 * — a PCR block held at 4C overnight is asked two questions the one switched
 * off is not. Columns outside a given row's set still render in the table,
 * recessed, because a table cannot drop a cell from one row only; `Calculator`
 * marks those not-applicable.
 *
 * A typed group leaves out the type itself, which its heading already says.
 */
const columnsForGroup = (
  columns: ServiceFormColumn[],
  group: Group,
  valuesOf: AddRowTableProps["valuesOf"],
): ServiceFormColumn[] => {
  const asked = new Set<string>();
  for (const row of group.rows) {
    const values = rowValuesByFieldKey(valuesOf(String(row.id)));
    for (const fieldKey of visibleFieldKeysFor(
      values[EQUIPMENT_TYPE_FIELD_KEY] ?? "",
      values,
    )) {
      asked.add(fieldKey);
    }
  }
  if (group.type) asked.delete(EQUIPMENT_TYPE_FIELD_KEY);
  return columns.filter((column) => asked.has(fieldKeyOf(column.id)));
};

/** Lists the kinds of equipment not on the form yet; picking one adds it. */
const AddTypeSelect = ({
  types,
  label,
  onPick,
}: {
  types: readonly EquipmentType[];
  label: string;
  onPick: (type: EquipmentType) => void;
}): JSX.Element => {
  const id = useId();
  return (
    <fieldset className="fieldset sm:max-w-sm">
      <legend id={id} className="fieldset-legend">
        {label}
      </legend>
      <select
        aria-labelledby={id}
        className="select select-bordered w-full"
        value=""
        onChange={(event) => {
          if (isEquipmentType(event.target.value)) onPick(event.target.value);
        }}
      >
        <option value="" disabled>
          Choose a type
        </option>
        {types.map((type) => (
          <option key={type} value={type}>
            {type}
          </option>
        ))}
      </select>
    </fieldset>
  );
};

export const HeavyEquipmentGroups = ({
  onAddRow,
  ...props
}: HeavyEquipmentGroupsProps): JSX.Element => {
  const { columns, rows, valuesOf, config, locked } = props;
  const rootRef = useRef<HTMLDivElement>(null);
  const [focusRowId, setFocusRowId] = useState<string | null>(null);

  // A new row lands in its group already typed, so its first question is the
  // next thing to answer. Waits for the row to render before focusing it.
  useEffect(() => {
    if (!focusRowId) return;
    const control = [
      ...(rootRef.current?.querySelectorAll<HTMLElement>(
        `[data-row-id="${CSS.escape(focusRowId)}"]`,
      ) ?? []),
    ].find(
      (el) => el.getClientRects().length > 0 && !el.hasAttribute("disabled"),
    );
    if (!control) return;
    control.scrollIntoView({ block: "center" });
    control.focus({ preventScroll: true });
    setFocusRowId(null);
  }, [focusRowId, rows]);

  const names = equipmentRowNames(
    rows.map((row) => ({
      id: String(row.id),
      label: row.label,
      values: valuesOf(String(row.id)),
    })),
  );

  const groups: Group[] = [];
  for (const row of rows) {
    const value = (
      rowValuesByFieldKey(valuesOf(String(row.id)))[EQUIPMENT_TYPE_FIELD_KEY] ??
      ""
    ).trim();
    const type = isEquipmentType(value) ? value : null;
    const key = type ?? "untyped";
    const named = { ...row, label: names.get(String(row.id)) ?? row.label };
    const group = groups.find((g) => g.key === key);
    if (group) group.rows.push(named);
    else groups.push({ key, type, rows: [named] });
  }
  // Rows saved before types were picked up front still need one picked. They
  // keep the type column, and go last so typed groups stay put.
  groups.sort((a, b) => Number(a.type === null) - Number(b.type === null));

  const canAdd = Boolean(onAddRow) && !locked;
  const add = (type: EquipmentType): void => {
    if (onAddRow) setFocusRowId(onAddRow(type));
  };
  const missingTypes = EQUIPMENT_TYPES.filter(
    (type) => !groups.some((group) => group.type === type),
  );

  return (
    <div ref={rootRef} className="space-y-6">
      {groups.length === 0 ? (
        <AddRowTable {...props} />
      ) : (
        groups.map((group) => {
          const { type } = group;
          const headingId = `equipment-${group.key}`;
          return (
            <section key={group.key} aria-labelledby={headingId}>
              <Card variant="secondary">
                <div className="card-body p-4 sm:p-6">
                  <h3
                    id={headingId}
                    className="card-title text-base sm:text-lg leading-tight"
                  >
                    {type ?? "Pick a type for this equipment"}
                    {group.rows.length > 1 && ` (${group.rows.length})`}
                  </h3>
                  <AddRowTable
                    {...props}
                    columns={columnsForGroup(columns, group, valuesOf)}
                    rows={group.rows}
                    // The minimum that matters is three kinds of equipment
                    // overall, which `validate` enforces, not a floor per group.
                    config={{
                      ...config,
                      minRows: 0,
                      showRowNumbers: group.rows.length > 1,
                    }}
                  />
                  {canAdd && type && (
                    <div className="card-actions">
                      <Button
                        variant="secondary"
                        type="button"
                        className="btn-sm"
                        onClick={() => add(type)}
                      >
                        <Plus className="h-4 w-4" aria-hidden="true" />
                        {`Add another ${type}`}
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            </section>
          );
        })
      )}

      {canAdd && missingTypes.length > 0 && (
        <AddTypeSelect
          types={missingTypes}
          label={
            groups.some((group) => group.type)
              ? "Add a different kind of equipment"
              : "Add equipment"
          }
          onPick={add}
        />
      )}
    </div>
  );
};
